import * as sdk from '@cirqll/sdk';
import { Provider } from '@cirqll/sdk/enum';
import { Capacitor } from '@capacitor/core';
import { nativeLogin, nativeLogout, waitForTokenHydration } from '~/modules/native_auth';

/** Native only: the web flow's callback is a page of its own. */
export { isCompletingLogin } from '~/modules/native_auth';

/** On native, waits for token hydration first (see ~/modules/native_auth.ts's waitForTokenHydration). */
export async function fetchCurrentUser() {
    if (Capacitor.isNativePlatform()) {
        await waitForTokenHydration();
    }

    return sdk.queryClient.query(sdk.auth.me());
}

/**
 * Native (Capacitor) builds have no shared cookie jar between the OS's in-app
 * browser and this app, so they can't use the web flow's plain redirect — see
 * ~/modules/native_auth.ts.
 */
export async function redirectToLogin(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
        await nativeLogin();
    } else {
        await sdk.redirectToLogin();
    }
}

export async function performLogout(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
        await nativeLogout();
    } else {
        await sdk.redirectToLogout();
    }
}

/**
 * Native builds can't open the hosted activation page with this session, so they log out:
 * the next login passes through it (core's EnsureEmailVerifiedForOAuth).
 */
export async function redirectToActivateAccount(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
        await nativeLogout();
    } else {
        sdk.redirectToActivateAccount();
    }
}

/** Authorizes an integration item, using integration.code for the redirect URL. */
export async function authorize(provider: Provider): Promise<void> {
    const { data: url } = await sdk.api.get<string>(`oauth/${provider}/authorize_url`);
    window.open(url, '_self');
}
